import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { Compass3D } from '../decor/compass/Compass3D'
import { Ruler3D } from '../decor/Ruler3D'
import { ConeCourseFeature3D } from '../features/cone/ConeCourseFeature3D'
import { FeatureDispatch } from '../features/FeatureDispatch'
import {
  get3DConeGeometry,
  radialConeGeometry,
} from '@asset-viz/utils/geometryDefines'
import { extractScale, Vec3 } from '@asset-viz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import {
  GeckoAssetModel,
  GeckoComponentRect,
  GeckoCone,
  GeckoConeCircumferentialWeld,
  GeckoConeRadialWeld,
  GeckoConicalPlate,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A cone, plotted in 3D or on a radial plane in 2D, with the center of its base at "position"
 * @param model               The cone component model
 * @param assetModel          The asset model for the asset that the cone is a part of
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */
export const ConeSurface3D: React.FC<
  {
    model: GeckoCone
    assetModel: GeckoAssetModel
    plantNorthCompassOrientation?: number
  } & Surface3DGroupProps
> = ({ model, assetModel, ...groupProps }) => {
  //----------------------------------------------------------------------------
  const { show3D } = useAssetVizInternal()
  const cone3dGeometry = React.useMemo(
    () =>
      get3DConeGeometry(
        model.innerRadius.num,
        model.outerRadius.num,
        model.coneHeight.num
      ),
    [model.innerRadius.num, model.outerRadius.num, model.coneHeight.num]
  )

  const geometry = React.useMemo(
    () => (show3D ? cone3dGeometry : radialConeGeometry),
    [show3D, cone3dGeometry]
  )

  const { bounds, scale } = React.useMemo(() => {
    // radial plot should always start at radius 0 and x range should always span 0 -> 360

    const bounds = GeckoComponentRect.CreateFromXYs(
      model.componentBounds.componentID,
      0,
      0,
      360,
      model.componentBounds.y.end.num,
      GeckoMeasurementUnit.Degrees,
      model.componentBounds.y.unit
    )

    return {
      bounds: bounds,
      scale: extractScale(groupProps.scale).multiply(
        new Vector3(
          show3D ? 1.0 : model.componentBounds.y.end.num,
          show3D ? 1.0 : model.componentBounds.y.end.num,
          1.0
        )
      ),
    }
  }, [model, groupProps.scale, show3D])

  const { plates, verticalWelds, horizontalWelds, courses } =
    React.useMemo(() => {
      const plates: Array<GeckoConicalPlate> =
        model instanceof GeckoCone ? model.platesArray : []

      const verticalWelds: Array<GeckoConeRadialWeld> =
        model instanceof GeckoCone ? model.coneRadialWeldsArray : []
      const horizontalWelds: Array<GeckoConeCircumferentialWeld> =
        model instanceof GeckoCone ? model.coneCircumferentialWeldsArray : []

      return {
        plates: plates,
        verticalWelds: verticalWelds,
        horizontalWelds: horizontalWelds,
        courses: model.coursesArray,
      }
    }, [model])

  const getCompassPosition = React.useCallback<(angle: number) => Vec3>(
    (angle) => {
      const radians = (angle * Math.PI) / 180
      return !show3D
        ? [-1.05 * Math.sin(radians), 1.05 * Math.cos(radians), 0]
        : [
            model.innerRadius.num * Math.cos(radians),
            model.coneHeight.num,
            model.innerRadius.num * Math.sin(radians),
          ]
    },
    [model.innerRadius.num, show3D, model.coneHeight.num]
  )

  //----------------------------------------------------------------------------

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      if (!show3D) {
        const radius = 1.05
        const radialXPosition = radius * -Math.sin((coord.num * Math.PI) / 180)
        const radialYPosition = radius * Math.cos((coord.num * Math.PI) / 180)
        return [radialXPosition, radialYPosition, 0]
      }
      const radius = model.outerRadius.num
      const radialXPosition = radius * -Math.cos((coord.num * Math.PI) / 180)
      const radialZPosition = radius * Math.sin((coord.num * Math.PI) / 180)
      return [radialXPosition, 0, radialZPosition]
    },
    [model.outerRadius.num, show3D]
  )

  //----------------------------------------------------------------------------

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      // Apply the true scaling last (override anything in groupProps)
      scale={scale}>
      <Compass3D
        componentModel={model}
        assetModel={assetModel}
        getRotationPosition={getCompassPosition}
      />
      {model instanceof GeckoCone
        ? courses.map((course) => (
            <ConeCourseFeature3D
              key={course.id}
              component={model}
              model={course}
            />
          ))
        : null}

      <FeatureDispatch features={[plates, verticalWelds, horizontalWelds]} />

      {/* Circumferential (x) ruler ---------------------------------------- */}
      <Ruler3D
        anchor='center'
        start={bounds.start.x}
        end={bounds.end.x}
        includeTicks={true}
        includeStart={true}
        includeEnd={false}
        getTickPosition={positionXTick}
      />
    </ModelSurface3D>
  )
}
